import { useState, useEffect, useMemo } from 'react';
import {
  BarChart3, X, RefreshCw, Search, Users, FileText, KeyRound, Ban, MoonStar, Wifi,
} from 'lucide-react';
import { statsAPI } from '../api';
import './Stats.css';

const shortName = (f) => f.replace(/\.json$/, '');
const shortKey = (k) => k.slice(0, 8);

const fmtWhen = (iso) => {
  if (!iso) return 'ни разу';
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'только что';
  if (mins < 60) return `${mins} мин назад`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)} ч назад`;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const dayLabel = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric' });
};

/* ── График активности за неделю ── */
/* Столбики рисуем в CSS, без библиотек: значений всего семь, а любая charting-библиотека
   тянет в бандл сотни килобайт ради одного экрана. */
function WeekChart({ daily }) {
  const max = Math.max(1, ...daily.map(d => d.keys));
  const today = daily.length ? daily[daily.length - 1].date : null;
  return (
    <div className="stats-chart">
      {daily.map((d) => (
        <div key={d.date} className={`chart-col${d.date === today ? ' is-today' : ''}`}>
          <div className="chart-val">{d.keys || ''}</div>
          <div className="chart-bar-wrap">
            <div className="chart-bar" style={{ height: `${Math.round((d.keys / max) * 100)}%` }} />
          </div>
          <div className="chart-label">{dayLabel(d.date)}</div>
        </div>
      ))}
    </div>
  );
}

function Stats({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('files');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await statsAPI.get();
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось загрузить статистику');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const files = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data.perFile;
    return data.perFile.filter(r =>
      r.file.toLowerCase().includes(q) || (r.groupName || '').toLowerCase().includes(q)
    );
  }, [data, query]);

  const t = data?.totals;

  return (
    <div className="stats-page">
      <div className="stats-header">
        <div className="stats-title">
          <BarChart3 size={18} /> Статистика
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading} title="Обновить">
          <RefreshCw size={14} className={loading ? 'spin' : ''} /> Обновить
        </button>
        <button className="btn btn-secondary btn-sm" onClick={onClose} title="Закрыть (Esc)">
          <X size={14} />
        </button>
      </div>

      {error && <div className="stats-error">{error}</div>}

      {t && (
        <>
          <div className="stats-tiles">
            <div className="stat-tile accent">
              <div className="tile-label"><Users size={13} /> Ключей обновило подписку сегодня</div>
              <div className="tile-value">{t.usedToday}</div>
            </div>
            <div className="stat-tile">
              <div className="tile-label"><FileText size={13} /> Подписок с активностью</div>
              <div className="tile-value">{t.filesActiveToday}<span className="tile-of"> / {t.filesTotal}</span></div>
            </div>
            <div className="stat-tile">
              <div className="tile-label"><KeyRound size={13} /> Всего ключей в системе</div>
              <div className="tile-value">{t.keysTotal}</div>
            </div>
            <div className={`stat-tile${t.blocked ? ' danger' : ''}`}>
              <div className="tile-label"><Ban size={13} /> Заблокировано за шеринг</div>
              <div className="tile-value">{t.blocked}</div>
            </div>
            <div className="stat-tile">
              <div className="tile-label"><MoonStar size={13} /> Ни разу не использованы</div>
              <div className="tile-value">{t.neverUsed}</div>
            </div>
          </div>

          <div className="stats-secondline">
            <span>За 7 дней заходило <b>{t.usedWeek}</b> ключей</span>
            <span>Молчат дольше недели: <b>{t.idle}</b></span>
            <span className="stats-note">
              Считается скачивание подписки приложением, а не подключение к VPN — оно идёт мимо сервера.
            </span>
          </div>

          <WeekChart daily={data.daily} />

          <div className="stats-tabs">
            <button className={tab === 'files' ? 'active' : ''} onClick={() => setTab('files')}>
              По подпискам <span className="cnt">{data.perFile.length}</span>
            </button>
            <button className={tab === 'silent' ? 'active' : ''} onClick={() => setTab('silent')}>
              Молчат <span className="cnt">{data.silent.length}</span>
            </button>
            <button className={tab === 'ips' ? 'active' : ''} onClick={() => setTab('ips')}>
              Один IP — много ключей <span className="cnt">{data.sharedIps.length}</span>
            </button>
            <button className={tab === 'suspects' ? 'active' : ''} onClick={() => setTab('suspects')}>
              Подозрения <span className="cnt">{(data.suspects || []).length}</span>
            </button>
            {tab === 'files' && (
              <div className="stats-search">
                <Search size={14} />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Поиск по файлу или группе"
                />
              </div>
            )}
          </div>

          <div className="stats-table-wrap">
            {tab === 'files' && (
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Подписка</th>
                    <th>Группа</th>
                    <th className="num">Ключей</th>
                    <th className="num">Сегодня</th>
                    <th className="num">За 7 дней</th>
                    <th className="num">Молчат</th>
                    <th>Последнее обращение</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((r) => (
                    <tr key={r.file} className={r.mode === 'nonactive' ? 'row-off' : ''}>
                      <td className="cell-name">{shortName(r.file)}</td>
                      <td className="cell-dim">{r.groupName || r.group || '—'}</td>
                      <td className="num">{r.keys}</td>
                      <td className={`num${r.usedToday ? ' hot' : ''}`}>{r.usedToday || '—'}</td>
                      <td className="num">{r.usedWeek || '—'}</td>
                      <td className="num cell-dim">{r.neverUsed || '—'}</td>
                      <td className="cell-dim">{fmtWhen(r.lastUsed)}</td>
                    </tr>
                  ))}
                  {!files.length && <tr><td colSpan={7} className="empty">Ничего не найдено</td></tr>}
                </tbody>
              </table>
            )}

            {tab === 'silent' && (
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Подписка</th>
                    <th>Ключ</th>
                    <th>Метка</th>
                    <th className="num">Молчит</th>
                    <th>Последний раз</th>
                  </tr>
                </thead>
                <tbody>
                  {data.silent.map((s) => (
                    <tr key={s.key}>
                      <td className="cell-name">{shortName(s.file)}</td>
                      <td className="cell-mono">{shortKey(s.key)}…</td>
                      <td className="cell-dim">{s.label || '—'}</td>
                      <td className="num">{s.daysSilent === null ? '—' : `${s.daysSilent} дн.`}</td>
                      <td className="cell-dim">{fmtWhen(s.lastUsed)}</td>
                    </tr>
                  ))}
                  {!data.silent.length && <tr><td colSpan={5} className="empty">Все ключи активны</td></tr>}
                </tbody>
              </table>
            )}

            {tab === 'suspects' && (
              <>
                <div className="stats-hint">
                  <Wifi size={13} /> Разбор истории обращений ключа за 3 дня. Один человек меняет
                  сеть последовательно; два устройства обновляются вперемешку — это и ловится.
                  Ключи не блокируются автоматически: смотри и решай сам, снять привязку можно
                  кнопкой Unbind в карточке файла.
                </div>
                <table className="stats-table">
                  <thead>
                    <tr>
                      <th>Подписка</th>
                      <th>Ключ</th>
                      <th>Метка</th>
                      <th>Почему подозрительно</th>
                      <th>Сети</th>
                      <th>Клиенты</th>
                      <th className="num">Запросов</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.suspects || []).map((s) => (
                      <tr key={s.key}>
                        <td className="cell-name">{shortName(s.file)}</td>
                        <td className="cell-mono">{shortKey(s.key)}…</td>
                        <td className="cell-dim">{s.label || '—'}</td>
                        <td>
                          {s.reasons.map(r => <span key={r} className="reason">{r}</span>)}
                        </td>
                        <td className="cell-mono cell-dim">{s.networks.join(', ')}</td>
                        <td className="cell-dim">{s.fingerprints.join(', ') || '—'}</td>
                        <td className="num">{s.requests}</td>
                      </tr>
                    ))}
                    {!(data.suspects || []).length && (
                      <tr><td colSpan={7} className="empty">Подозрительных ключей нет</td></tr>
                    )}
                  </tbody>
                </table>
              </>
            )}

            {tab === 'ips' && (
              <>
                <div className="stats-hint">
                  <Wifi size={13} /> Один адрес у нескольких ключей — это ещё не нарушение: семья,
                  общий Wi-Fi или NAT оператора выглядят так же. Список — повод посмотреть, не более.
                </div>
                <table className="stats-table">
                  <thead>
                    <tr>
                      <th>IP</th>
                      <th className="num">Ключей</th>
                      <th>Какие именно</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sharedIps.map((row) => (
                      <tr key={row.ip}>
                        <td className="cell-mono">{row.ip}</td>
                        <td className="num hot">{row.count}</td>
                        <td className="cell-dim">
                          {row.keys.map(k => `${shortName(k.file)}/${shortKey(k.key)}`).join(', ')}
                        </td>
                      </tr>
                    ))}
                    {!data.sharedIps.length && <tr><td colSpan={3} className="empty">Совпадений нет</td></tr>}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </>
      )}

      {loading && !data && <div className="stats-loading">Загрузка…</div>}
    </div>
  );
}

export default Stats;
